import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft,
  FileText,
  Heart,
  Brain,
  Zap,
  Activity,
  Clock,
  AlertTriangle,
  Pill,
  CheckCircle,
  ChevronRight,
  Wind,
  Thermometer,
  Utensils,
  ShieldAlert
} from 'lucide-react';

import { API_URL } from '../config';

const protocolIcons = {
  'ACR': Heart,
  'AVC': Brain,
  'EME': Zap,
  'CHOC': Activity,
  'RESPI': Wind,
  'FIEVRE': Thermometer,
  'DIGESTIF': Utensils,
  'DOULEUR': Pill,
  'METABOLIQUE': Activity,
  'INFECTIEUX': ShieldAlert,
};

const protocolColors = {
  'ACR': { color: 'text-red-400', bg: 'bg-red-400/10', border: 'border-red-400/30' },
  'AVC': { color: 'text-purple-400', bg: 'bg-purple-400/10', border: 'border-purple-400/30' },
  'EME': { color: 'text-yellow-400', bg: 'bg-yellow-400/10', border: 'border-yellow-400/30' },
  'CHOC': { color: 'text-orange-400', bg: 'bg-orange-400/10', border: 'border-orange-400/30' },
  'RESPI': { color: 'text-cyan-400', bg: 'bg-cyan-400/10', border: 'border-cyan-400/30' },
  'FIEVRE': { color: 'text-red-300', bg: 'bg-red-300/10', border: 'border-red-300/30' },
  'DIGESTIF': { color: 'text-green-400', bg: 'bg-green-400/10', border: 'border-green-400/30' },
  'DOULEUR': { color: 'text-pink-400', bg: 'bg-pink-400/10', border: 'border-pink-400/30' },
  'METABOLIQUE': { color: 'text-blue-400', bg: 'bg-blue-400/10', border: 'border-blue-400/30' },
  'INFECTIEUX': { color: 'text-amber-400', bg: 'bg-amber-400/10', border: 'border-amber-400/30' },
};

export default function ProtocolDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [protocol, setProtocol] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);

  useEffect(() => {
    fetchProtocol();
  }, [id]);

  const fetchProtocol = async () => {
    try {
      const response = await fetch(`${API_URL}/api/protocols/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setProtocol(data.protocol);
      }
    } catch (error) {
      console.error('Error fetching protocol:', error);
    } finally {
      setLoading(false);
    }
  };

  const markStepComplete = (stepIndex) => {
    if (!completedSteps.includes(stepIndex)) {
      setCompletedSteps([...completedSteps, stepIndex]);
    }
    if (stepIndex < (protocol?.steps?.length || 0) - 1) {
      setCurrentStep(stepIndex + 1);
    }
  };

  if (loading) {
    return (
      <div className="p-4 md:p-6">
        <div className="skeleton h-8 w-48 mb-4"></div>
        <div className="skeleton h-64 rounded-xl"></div>
      </div>
    );
  }

  if (!protocol) {
    return (
      <div className="p-4 md:p-6 text-center py-12">
        <p className="text-[hsl(var(--text-dim))]">Protocole non trouvé</p>
        <Button onClick={() => navigate('/protocols')} className="mt-4">
          Retour aux protocoles
        </Button>
      </div>
    );
  }

  const Icon = protocolIcons[protocol.type] || FileText;
  const colors = protocolColors[protocol.type] || { color: 'text-cyan-400', bg: 'bg-cyan-400/10', border: 'border-cyan-400/30' };

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={() => navigate(-1)}
        className="text-[hsl(var(--text-dim))] hover:text-cyan-300"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Retour
      </Button>

      {/* Header */}
      <div className={`glass-card rounded-xl p-6 ${colors.border}`}>
        <div className="flex items-start gap-4">
          <div className={`w-16 h-16 rounded-xl ${colors.bg} flex items-center justify-center`}>
            <Icon className={`w-8 h-8 ${colors.color}`} />
          </div>
          <div className="flex-1">
            <Badge className={`${colors.bg} ${colors.color} ${colors.border} mb-2`}>
              {protocol.type}
            </Badge>
            <h1 className="text-2xl font-bold">{protocol.title}</h1>
            <p className="text-[hsl(var(--text-dim))] mt-1">{protocol.description}</p>
          </div>
        </div>
      </div>

      {/* Progress */}
      <div className="glass-card rounded-xl p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-[hsl(var(--text-dim))]">Progression</span>
          <span className="text-sm text-cyan-400 font-mono">
            {completedSteps.length}/{protocol.steps?.length || 0}
          </span>
        </div>
        <div className="h-2 bg-[hsl(var(--bg))] rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-cyan-400 to-cyan-300 transition-all duration-300"
            style={{ width: `${(completedSteps.length / (protocol.steps?.length || 1)) * 100}%` }}
          ></div>
        </div>
      </div>

      {/* Warnings */}
      {protocol.warnings?.length > 0 && (
        <div className="glass-card rounded-xl p-4 border-amber-500/30">
          <div className="flex items-center gap-2 text-amber-400 mb-2">
            <AlertTriangle className="w-5 h-5" />
            <span className="font-medium">Points d'attention</span>
          </div>
          <ul className="space-y-1">
            {protocol.warnings.map((warning, i) => (
              <li key={i} className="text-sm text-amber-300 flex items-start gap-2">
                <AlertTriangle className="w-3 h-3 mt-1 flex-shrink-0" />
                {warning}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Steps */}
      <div className="space-y-3" data-testid="protocol-accordion">
        {protocol.steps?.map((step, index) => {
          const isCompleted = completedSteps.includes(index);
          const isCurrent = currentStep === index;
          
          return (
            <div 
              key={index}
              className={`protocol-card overflow-hidden transition-all ${isCurrent ? 'ring-2 ring-cyan-400/50' : ''}`}
              data-testid="protocol-step"
            >
              <div 
                className={`p-4 cursor-pointer ${isCompleted ? 'bg-green-400/10' : isCurrent ? colors.bg : ''}`}
                onClick={() => setCurrentStep(index)}
              >
                <div className="flex items-center gap-3">
                  <div className={`protocol-step-number ${isCompleted ? 'bg-green-400/20 text-green-400' : isCurrent ? `${colors.bg} ${colors.color}` : ''}`}>
                    {isCompleted ? <CheckCircle className="w-4 h-4" /> : step.order}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold">{step.title}</h3>
                    {step.duration && (
                      <span className="text-xs text-[hsl(var(--text-dim))] flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {step.duration}
                      </span>
                    )}
                  </div>
                  <ChevronRight className={`w-5 h-5 text-[hsl(var(--muted))] transition-transform ${isCurrent ? 'rotate-90' : ''}`} />
                </div>
              </div>
              
              {isCurrent && (
                <div className="p-4 border-t border-[hsl(var(--border))] animate-scale-in">
                  <p className="text-[hsl(var(--text))] whitespace-pre-line mb-4">{step.content}</p>
                  <Button
                    onClick={() => markStepComplete(index)}
                    className={`w-full ${colors.bg} ${colors.color} ${colors.border} hover:opacity-80`}
                    data-testid="protocol-next-button"
                  >
                    {index === (protocol.steps?.length || 0) - 1 ? (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Terminer le protocole
                      </>
                    ) : (
                      <>
                        Suivant
                        <ChevronRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Medications */}
      {protocol.medications?.length > 0 && (
        <div className="glass-card rounded-xl p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Pill className="w-5 h-5 text-cyan-400" />
            Médicaments associés
          </h2>
          <div className="flex flex-wrap gap-2">
            {protocol.medications.map((med, i) => (
              <Badge key={i} variant="outline" className="text-cyan-300 border-cyan-400/30">
                <Pill className="w-3 h-3 mr-1" />
                {med}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Completion */}
      {completedSteps.length === (protocol.steps?.length || 0) && (
        <div className="glass-card rounded-xl p-6 text-center border-green-500/30 animate-scale-in">
          <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-green-400">Protocole terminé</h3>
          <p className="text-sm text-[hsl(var(--text-dim))] mt-1">Toutes les étapes ont été complétées</p>
        </div>
      )}
    </div>
  );
}
